/**
 * Approval framework's wire types. Shared by the API service
 * (`apps/api/src/core/approvals/`), the typed client
 * (`@clockwise/clients/approval-{policies,instances}`), and the web UI
 * (policy admin form, status badges, approver picker).
 *
 * One file = one source of truth so the type definitions never drift
 * between server (the contract) and client (the consumer). The server
 * keeps its own stricter Zod schemas (with min/max constraints) in
 * `validators.ts`; this module exports the bare constants + TypeScript
 * type aliases the client side needs.
 *
 * When the framework grows a new status, transition, source kind, or
 * registered object type, update it here once.
 */

// ── Instance status (`approval/event.status` union + 'pending'/'in_progress') ──

export const APPROVAL_INSTANCE_STATUSES = [
  'pending',
  'in_progress',
  'approved',
  'rejected',
  'failed',
  'canceled',
] as const;
export type ApprovalInstanceStatus = (typeof APPROVAL_INSTANCE_STATUSES)[number];

// ── Approver-row status ──

export const APPROVER_STATUSES = ['pending', 'approved', 'rejected', 'canceled'] as const;
export type ApproverStatus = (typeof APPROVER_STATUSES)[number];

// ── Step approval type (any-N / all-N) ──

export const APPROVAL_STEP_TYPES = ['any', 'all'] as const;
export type ApprovalStepType = (typeof APPROVAL_STEP_TYPES)[number];

// ── Approver source (discriminated union) ──

export const APPROVER_TYPE_KINDS = ['users_manager', 'customer_team_lead'] as const;
export type ApproverTypeKind = (typeof APPROVER_TYPE_KINDS)[number];

export type ApproverSource =
  | { kind: 'user'; userId: string }
  | { kind: 'system_role'; roleId: string }
  | { kind: 'type'; type: ApproverTypeKind };

// ── Step shape ──

export interface ApprovalStepInput {
  approvalType: ApprovalStepType;
  approverSources: ApproverSource[];
}

// ── Conditions ──

export const CONDITION_OPERATORS = [
  'eq',
  'neq',
  'in',
  'not_in',
  'gt',
  'gte',
  'lt',
  'lte',
  'between',
] as const;
export type ConditionOperator = (typeof CONDITION_OPERATORS)[number];

export interface ConditionItem {
  field: string;
  op: ConditionOperator;
  value: unknown;
}

export interface ApprovalConditions {
  logic: 'all' | 'any';
  items: ConditionItem[];
}

// ── Object-type registry ─────────────────────────────────────────────────
// Every entity that participates in the approvals framework registers
// itself here. The web UI's policy admin uses this list for the "Applies
// to" dropdown; the backend resolvers + condition evaluator read the
// same names from the policy row's `object_type` column.
//
// When a new entity adopts approvals (expenses, vendor invoices, …)
// extend this tuple — no other UI code needs to change.

export const APPROVAL_OBJECT_TYPES = [
  { value: 'demo', label: 'Demo' },
  { value: 'timesheet', label: 'Timesheet' },
] as const;
export type ApprovalObjectType = (typeof APPROVAL_OBJECT_TYPES)[number]['value'];

// ── System resolution codes ──────────────────────────────────────────────
// `approval_instances.resolution_reason` carries two very different kinds
// of content, distinguished by the instance's status:
//
//   - `rejected` → free text written by a HUMAN approver ("hours look
//     wrong, split by project"). Shown to the submitter verbatim.
//   - `failed`   → a SYSTEM outcome. No human wrote it, and the prose we
//     used to store ("No eligible approvers for step 1 (1 routed, none
//     eligible to decide)") was written for operators but rendered to
//     employees, who have no idea what a routed step is.
//
// So the failed case stores one of these stable codes instead, and each
// consumer renders copy for ITS audience. Codes are also matchable —
// consumers previously asserted on the exact prose, which broke whenever
// the wording changed.
//
// The two codes are deliberately separate because they need DIFFERENT
// admin fixes:
//
//   - NO_APPROVERS_ROUTED         → the policy/team points at nobody.
//                                   Fix the customer team or the policy.
//   - ROUTED_APPROVERS_INELIGIBLE → it pointed at real people, none of
//                                   whom may decide (missing permission,
//                                   or past their employment window).
//                                   Fix that person's role or the routing.
//
// Collapsing them into one code would tell an admin that something is
// wrong without telling them what to change. WHICH users were dropped is
// deliberately NOT encoded here — that would leak org structure to the
// submitter; it goes to the `warn` log alongside tenant, object and step.

export const APPROVAL_RESOLUTION_CODES = [
  'NO_APPROVERS_ROUTED',
  'ROUTED_APPROVERS_INELIGIBLE',
] as const;
export type ApprovalResolutionCode = (typeof APPROVAL_RESOLUTION_CODES)[number];

/**
 * Is this `resolution_reason` one of our system codes?
 *
 * Rows written before this shipped still carry the old prose, and there
 * is no backfill — so every renderer must fall back to showing the raw
 * value when this returns false. That fallback is permanent, not
 * transitional: a human rejection reason is also "not a code".
 */
export function isApprovalResolutionCode(value: unknown): value is ApprovalResolutionCode {
  return (
    typeof value === 'string' && (APPROVAL_RESOLUTION_CODES as readonly string[]).includes(value)
  );
}
